import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_IMPORT_KINDS, IMPORT_COLUMNS, IMPORT_LIMITS, type ImportKind, type ImportReport } from '@lims/shared-types';
import { importsApi } from '../../api/endpoints.js';
import { isolate } from '../quality/operations-ui.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  PageHeader,
  Panel,
  SelectField,
  TextAreaField,
  useToast,
} from '../../components/ui/index.js';

/**
 * Data import (Phase 12, D175).
 *
 * VALIDATE FIRST. The check writes nothing and reports every row. Importing is
 * offered only once a check found no rejected row, and the server re-checks
 * everything anyway: it writes every row in one transaction, or nothing. The
 * page reports exactly what the server says was created.
 */
export function ImportPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const [kind, setKind] = useState<ImportKind>('clients');
  const [csv, setCsv] = useState('');
  const [report, setReport] = useState<ImportReport | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  // The browser draws a native file input's label in ITS language, not ours;
  // the input is hidden and a translated button opens it.
  const fileInput = useRef<HTMLInputElement>(null);

  const reset = () => setReport(null);

  const validate = useMutation({
    mutationFn: () => importsApi.validate(kind, csv),
    onSuccess: setReport,
  });
  const commit = useMutation({
    mutationFn: () => importsApi.commit(kind, csv),
    onSuccess: async (result) => {
      setReport(result);
      show({
        tone: 'success',
        message: t('import.committed', { created: String(result.createdCount), total: String(result.totalRows) }),
      });
      await queryClient.invalidateQueries();
    },
  });

  const readFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setCsv(await file.text());
    reset();
  };

  const clean =
    report !== null && !report.committed && report.fileIssues.length === 0 && report.rejectedRows === 0 && report.totalRows > 0;

  return (
    <>
      <PageHeader
        title={t('import.title')}
        subtitle={t('import.subtitle')}
        breadcrumbs={[{ label: t('nav.settings'), to: '/settings' }, { label: t('nav.dataImport') }]}
      />
      <Alert tone="info" title={t('import.allOrNothing')} description={t('import.noScience')} />
      <div className="mt-stack flex flex-col gap-gutter">
        <Panel title={t('import.fileTitle')}>
          <div className="flex flex-col gap-stack">
            <SelectField
              label={t('import.kind')}
              value={kind}
              onChange={(value) => {
                if (value) setKind(value as ImportKind);
                reset();
              }}
              options={ALL_IMPORT_KINDS.map((value) => ({ value, label: t(`importKind.${value}`) }))}
            />
            <div className="text-body text-default-700">
              <p>{t('import.columns')}</p>
              <p className="break-all font-mono text-caption text-default-800" dir="ltr">
                {IMPORT_COLUMNS[kind].join(',')}
              </p>
            </div>
            <p className="text-caption text-default-600">
              {t('import.limits', { rows: String(IMPORT_LIMITS.MAX_ROWS) })}
            </p>
            <div className="flex flex-wrap items-center gap-inline">
              <input
                ref={fileInput}
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                onChange={(event) => void readFile(event)}
              />
              <ActionButton priority="secondary" onPress={() => fileInput.current?.click()}>
                {t('import.chooseFile')}
              </ActionButton>
              <span className="text-body text-default-700" dir="ltr">
                {fileName ?? ''}
              </span>
            </div>
            <TextAreaField
              label={t('import.csv')}
              description={t('import.csvHint')}
              textDirection="ltr"
              value={csv}
              onChange={(value) => {
                setCsv(value);
                reset();
              }}
            />
            {validate.error ? <ErrorNotice error={validate.error} /> : null}
            {commit.error ? <ErrorNotice error={commit.error} /> : null}
            <ActionBar>
              <ActionButton
                priority="secondary"
                isDisabled={!csv.trim()}
                isLoading={validate.isPending}
                onPress={() => validate.mutate()}
              >
                {t('import.validate')}
              </ActionButton>
              <ActionButton
                priority="primary"
                isDisabled={!clean}
                isLoading={commit.isPending}
                onPress={() => commit.mutate()}
              >
                {t('import.commit')}
              </ActionButton>
            </ActionBar>
          </div>
        </Panel>

        {report ? (
          <Panel title={t('import.reportTitle')}>
            <div className="flex flex-col gap-stack">
              <p className="text-body text-default-900">
                {t('import.summary', {
                  total: String(report.totalRows),
                  accepted: String(report.acceptedRows),
                  rejected: String(report.rejectedRows),
                })}
              </p>
              {report.committed ? (
                <Alert
                  tone="success"
                  title={t('import.committed', { created: String(report.createdCount), total: String(report.totalRows) })}
                />
              ) : report.fileIssues.length > 0 || report.rejectedRows > 0 ? (
                <Alert tone="warning" title={t('import.nothingWritten')} />
              ) : null}
              {report.fileIssues.length > 0 ? (
                <ul className="list-disc ps-6 text-body text-default-800">
                  {report.fileIssues.map((issue, index) => (
                    <li key={index}>
                      {t(`importIssue.${issue.code}`, { defaultValue: t('importIssue.unknown') })}
                      {issue.column ? <span dir="ltr"> ({issue.column})</span> : null}
                    </li>
                  ))}
                </ul>
              ) : null}
              {report.rows.some((row) => !row.accepted) ? (
                <ul className="flex flex-col gap-1 text-body text-default-800">
                  {report.rows
                    .filter((row) => !row.accepted)
                    .map((row) => (
                      <li key={row.row}>
                        {t('import.rowLabel', { row: isolate(row.row) })}{' '}
                        {row.issues
                          .map(
                            (issue) =>
                              `${t(`importIssue.${issue.code}`, { defaultValue: t('importIssue.unknown') })}${issue.column ? ` ${isolate(`(${issue.column})`)}` : ''}`,
                          )
                          .join('; ')}
                      </li>
                    ))}
                </ul>
              ) : null}
            </div>
          </Panel>
        ) : null}
      </div>
    </>
  );
}
